import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getUserPermissions } from 'src/redux/reducers/user';
import { Permissions } from 'src/auth/permissions/constants';
import { checkForPermission, hasAnyPermission } from 'src/auth/permissions/utils';

type PermissionLike = Permissions.PermissionsList | string;

/**
 * Punto de lectura de permisos del portal "Mis Pagos".
 *
 * Los consumidores existentes (authGuard, LayoutDefault, DrawerInfoTransaction)
 * siguen usando `checkForPermission` directamente; este hook es para el portal
 * nuevo y expone chequeos memoizados sobre los permisos del store.
 */
export const usePermissions = () => {
  const permissions: string[] = useSelector(getUserPermissions);

  return useMemo(() => {
    const list = permissions ?? [];
    const has = (permission: PermissionLike) => checkForPermission(list, permission as string);
    const hasAny = (permissionsToCheck: PermissionLike[]) => hasAnyPermission(list, permissionsToCheck as string[]);

    return {
      has,
      hasAny,
      canRead: has(Permissions.PermissionsList.ReadTransactions),
      canDownload: has(Permissions.PermissionsList.DownloadTransactions),
      canManage: has(Permissions.PermissionsList.ManageTransactions)
    };
  }, [permissions]);
};

export default usePermissions;
